import FontAwesome from 'react-native-vector-icons/FontAwesome';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';
import {
    ActivityIndicator,
    Image,
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    useWindowDimensions,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getThemeColors } from '../utils/themeStyles';
import { useMissions, ActiveMissionData, MarketplaceMission, TrialMission } from '../context/MissionsContext';
import { STORY_CHARACTERS } from '../data/storyCharacters';

const MISSION_STORY_KEY_MAP: Record<number, string> = {
  1: 'egocrisis', 101: 'egocrisis',
  2: 'foundation_gamble', 102: 'architecture_under_fire', 201: 'architecture_under_fire',
  3: 'data_storm',
  109: 'cloud_heist',
  110: 'neural_override',
  111: 'pipeline_inferno',
  104: 'quantum_gambit', 202: 'quantum_gambit',
  105: 'learning_machine',
  106: 'ink_protocol', 203: 'ink_protocol',
  108: 'arabic_modern', 112: 'arabic_modern', 113: 'arabic_modern',
  114: 'arabic_modern', 115: 'arabic_modern',
};

function getMissionAvatars(missionId: number): any[] {
  const key = MISSION_STORY_KEY_MAP[missionId];
  if (!key) return [];
  return (STORY_CHARACTERS[key] ?? []).slice(0, 4).map(c => c.avatar);
}

interface FilterState {
  domain: string;
  provider: string; // pour Cloud : Azure / AWS / GCP… (choisi avant la certif)
  certification: string;
}

const CERT_GROUPS: Record<string, Array<{ label: string; items: string[] }>> = {
  Cloud: [
    { label: 'Azure', items: ['AZ-900', 'AZ-104', 'AZ-305', 'AZ-400', 'AZ-500', 'AZ-700', 'AZ-220'] },
    { label: 'AWS', items: ['AWS Cloud Practitioner', 'AWS Developer', 'AWS DevOps', 'AWS AI Practitioner'] },
    { label: 'GCP', items: ['GCP Cloud Engineer', 'GCP Cloud Architect'] },
    { label: 'Data', items: ['DP-900', 'DP-203', 'DP-300'] },
    { label: 'Security', items: ['SC-900'] },
    { label: 'Power Platform', items: ['PL-900'] },
    { label: 'AI', items: ['AI-900'] },
  ],
  Science: [
    { label: 'Topics', items: ['Python', 'Data Science', 'Machine Learning'] },
  ],
  Languages: [
    { label: 'Languages', items: ['French', 'English', 'Spanish', 'Arabic'] },
  ],
};

const CLOUD_CERT_SET = new Set(CERT_GROUPS.Cloud.flatMap(group => group.items.map(item => item.trim().toUpperCase())));
const SCIENCE_CERT_SET = new Set(CERT_GROUPS.Science.flatMap(group => group.items.map(item => item.trim().toUpperCase())));
const LANGUAGE_CERT_SET = new Set(CERT_GROUPS.Languages.flatMap(group => group.items.map(item => item.trim().toUpperCase())));

export default function MissionsScreen() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const { user } = useAuth();
  const colors = getThemeColors(isDarkTheme);
  const { activeMissions, trialMissions, startTrial, cancelTrial, addToCart, isInTrial, isInCart, isOwned, allMarketplaceMissions, marketplaceLoading, cartItems } = useMissions();
  // Badges dont l'image ne charge pas (asset manquant sur le blob) -> on les
  // masque au lieu d'afficher une image cassée.
  const [failedBadges, setFailedBadges] = useState<Set<number>>(new Set());
  const markBadgeFailed = (id: number) =>
    setFailedBadges(prev => (prev.has(id) ? prev : new Set(prev).add(id)));

  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const isWebDesktop = Platform.OS === 'web' && isDesktop;

  const [activeTab, setActiveTab] = useState<'active' | 'trial' | 'marketplace'>(user ? 'active' : 'marketplace');
  const [tagFilters, setTagFilters] = useState<FilterState>({
    domain: '', provider: '', certification: ''
  });
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return '#10B981';
      case 'Medium': return '#F59E0B';
      case 'Hard': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const renderStars = (rating: number) => {
    const stars: React.ReactNode[] = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    for (let i = 0; i < fullStars; i++) stars.push(<Ionicons key={`star-${i}`} name="star" size={16} color={colors.warning} />);
    if (hasHalfStar) stars.push(<Ionicons key="star-half" name="star-half" size={16} color={colors.warning} />);
    const remaining = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < remaining; i++) stars.push(<Ionicons key={`star-empty-${i}`} name="star-outline" size={16} color={colors.borderSecondary} />);
    return stars;
  };

  const normalize = (value?: string) => (value ?? '').trim();
  const normalizeUpper = (value?: string) => normalize(value).toUpperCase();
  const normalizeLower = (value?: string) => normalize(value).toLowerCase();

  const getMissionDomain = (mission: MarketplaceMission): FilterState['domain'] => {
    const taggedDomain = normalizeLower(mission.tags.domain);
    if (taggedDomain === 'cloud') return 'Cloud';
    if (taggedDomain === 'science') return 'Science';
    if (taggedDomain === 'languages') return 'Languages';

    const category = normalizeLower(mission.category);
    if (category === 'cloud computing') return 'Cloud';
    if (category === 'data science' || category === 'physics') return 'Science';
    if (category === 'language learning' || category === 'language arts') return 'Languages';

    const cert = normalizeUpper(mission.tags.certification);
    if (CLOUD_CERT_SET.has(cert)) return 'Cloud';
    if (SCIENCE_CERT_SET.has(cert)) return 'Science';
    if (LANGUAGE_CERT_SET.has(cert)) return 'Languages';

    if (
      cert.startsWith('AZ-') ||
      cert.startsWith('AWS') ||
      cert.startsWith('GCP') ||
      cert.startsWith('DP-') ||
      cert.startsWith('SC-') ||
      cert.startsWith('PL-') ||
      cert.startsWith('AI-')
    ) {
      return 'Cloud';
    }

    return 'Science';
  };

  const handleFilterChange = (level: keyof FilterState, value: string) => {
    setTagFilters(prev => {
      const next = { ...prev, [level]: value };
      // Changer de niveau réinitialise les niveaux plus profonds.
      if (level === 'domain') { next.provider = ''; next.certification = ''; }
      if (level === 'provider') { next.certification = ''; }
      return next;
    });
  };

  const clearFilters = () => setTagFilters({ domain: '', provider: '', certification: '' });

  const getFilteredMissions = () => {
    const selectedDomain = normalize(tagFilters.domain);
    const selectedCert = normalizeUpper(tagFilters.certification);

    return allMarketplaceMissions.filter(mission => {
      // Masque les missions déjà acquises (DB-driven via /me/stories).
      if (isOwned(mission.id)) return false;

      const missionDomain = getMissionDomain(mission);
      const cert = normalizeUpper(mission.tags.certification);
      const tech = normalizeUpper(mission.tags.technology);

      if (selectedDomain && missionDomain !== selectedDomain) return false;
      if (selectedCert) {
        // Certif précise sélectionnée.
        if (cert !== selectedCert && tech !== selectedCert) return false;
      } else if (tagFilters.provider) {
        // Provider sélectionné (sans certif) : la mission doit appartenir au groupe.
        const grp = (CERT_GROUPS[tagFilters.domain] || []).find(g => g.label === tagFilters.provider);
        const set = new Set((grp?.items || []).map(i => i.trim().toUpperCase()));
        const provUpper = normalizeUpper(tagFilters.provider);
        if (!set.has(cert) && !set.has(tech) && tech !== provUpper) return false;
      }
      return true;
    });
  };

  const hasActiveFilters = !!(tagFilters.domain || tagFilters.provider || tagFilters.certification);

  const renderActiveMissionCard = (mission: ActiveMissionData) => (
    <View key={mission.id} style={[styles.missionCard, { backgroundColor: colors.cardBackground }]}>
      <View style={styles.missionHeader}>
        <View style={styles.missionTitleRow}>
          <View style={styles.missionTitleFlex}>
            <Text style={[styles.missionTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
            {mission.subtitle && (
              <Text style={[styles.missionSubtitle, { color: colors.textSecondary }]}>{mission.subtitle}</Text>
            )}
          </View>
          <View style={styles.missionBadgesFlex}>
            {mission.certification && (
              <View style={styles.certPill}>
                <Text style={styles.certPillText}>{mission.certification}</Text>
              </View>
            )}
          </View>
        </View>
        <Text style={[styles.missionDescription, { color: colors.textSecondary }]} numberOfLines={2}>{mission.description}</Text>
      </View>
      {mission.coverImage ? (
        <Image source={mission.coverImage} style={styles.missionCoverThumb} resizeMode="cover" />
      ) : (() => { const avs = getMissionAvatars(mission.id); return avs.length > 0 ? (
        <View style={[styles.missionCoverThumb, styles.avatarBand, { backgroundColor: '#0F172A' }]}>
          <View style={styles.avatarBandRow}>
            {avs.map((av, idx) => (
              <View key={idx} style={[styles.avatarBandCircle, idx > 0 && styles.avatarBandOverlap]}>
                <Image source={av} style={styles.avatarBandImg} resizeMode="cover" />
              </View>
            ))}
          </View>
        </View>
      ) : null; })()}
      <View style={styles.missionMeta}>
        <View style={styles.difficultyContainer}>
          <View style={[styles.difficultyDot, { backgroundColor: getDifficultyColor(mission.difficulty) }]} />
          <Text style={[styles.difficultyText, { color: colors.textSecondary }]}>{mission.difficulty}</Text>
        </View>
        <View style={styles.timeContainer}>
          <Ionicons name="time" size={12} color={colors.iconSecondary} />
          <Text style={[styles.timeText, { color: colors.textSecondary }]}> {mission.timeLeft}</Text>
        </View>
      </View>
      <View style={styles.progressContainer}>
        <View style={styles.progressHeader}>
          <Text style={[styles.progressLabel, { color: colors.textSecondary }]}>Progress</Text>
          <Text style={[styles.progressPercentage, { color: colors.textPrimary }]}>{mission.progress}%</Text>
        </View>
        <View style={[styles.progressBar, { backgroundColor: colors.border }]}>
          <View style={[styles.progressFill, { width: `${mission.progress}%` as any, backgroundColor: colors.primary }]} />
        </View>
      </View>
      <View style={styles.actionButtons}>
        <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.actionButtonBackground }]} onPress={() => navigation.navigate('Chapters' as never, { missionId: mission.id } as never)}>
          <Ionicons name="book" size={16} color={colors.iconPrimary} />
          <Text style={[styles.actionButtonText, { color: colors.actionButtonText }]}>Chapters</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.actionButtonBackground }]} onPress={() => navigation.navigate('MissionChat' as never, { missionId: mission.id } as never)}>
          <Ionicons name="chatbox" size={16} color={colors.iconPrimary} />
          <Text style={[styles.actionButtonText, { color: colors.actionButtonText }]}>Chat</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.actionButtonBackground }]} onPress={() => navigation.navigate('MissionCards' as never, { missionId: mission.id } as never)}>
          <Ionicons name="document" size={16} color={colors.iconPrimary} />
          <Text style={[styles.actionButtonText, { color: colors.actionButtonText }]}>Cards</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.actionButtonBackground }]} onPress={() => navigation.navigate('UserStories' as never, { missionId: mission.id } as never)}>
          <Ionicons name="people" size={16} color={colors.iconPrimary} />
          <Text style={[styles.actionButtonText, { color: colors.actionButtonText }]}>Stories</Text>
        </TouchableOpacity>
      </View>
      <TouchableOpacity style={[styles.detailsButton, { borderColor: colors.borderSecondary }]} onPress={() => navigation.navigate('MissionDetail' as never, { missionId: mission.id } as never)}>
        <Ionicons name="information-circle-outline" size={16} color={colors.textSecondary} />
        <Text style={[styles.detailsButtonText, { color: colors.textSecondary }]}>Plus de details</Text>
      </TouchableOpacity>
      {mission.progress === 100 ? (
        <TouchableOpacity style={[styles.claimButton, { backgroundColor: colors.primary }]}>
          <Ionicons name="trophy" size={16} color={colors.badgeText} />
          <Text style={[styles.claimButtonText, { color: colors.badgeText }]}> Claim Reward</Text>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          style={[styles.continueButton, { backgroundColor: colors.primary }]}
          onPress={() => navigation.navigate('ChapterDetail' as never, { missionId: mission.id, chapterId: 1 } as never)}
        >
          <Text style={[styles.continueButtonText, { color: colors.badgeText }]}>Continuer</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderTrialMissionCard = (mission: TrialMission) => (
    <View key={mission.id} style={[styles.missionCard, { backgroundColor: colors.cardBackground }]}>
      <View style={styles.missionHeader}>
        <View style={styles.missionTitleRow}>
          <View style={styles.missionTitleFlex}>
            <Text style={[styles.missionTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
            {mission.subtitle && (
              <Text style={[styles.missionSubtitle, { color: colors.textSecondary }]}>{mission.subtitle}</Text>
            )}
          </View>
          <View style={styles.missionBadgesFlex}>
            <View style={[styles.trialBadge, { backgroundColor: colors.warning }]}>
              <Text style={[styles.trialText, { color: colors.badgeText }]}>Trial</Text>
            </View>
            {mission.remainingActions > 0 && (
              <View style={[styles.certPill, { backgroundColor: colors.warning + '33' }]}>
                <Text style={[styles.certPillText, { color: colors.warning }]}>{mission.remainingActions}</Text>
              </View>
            )}
          </View>
        </View>
        <Text style={[styles.missionDescription, { color: colors.textSecondary }]} numberOfLines={2}>{mission.description}</Text>
      </View>
      {mission.coverImage ? (
        <Image source={mission.coverImage} style={styles.missionCoverThumb} resizeMode="cover" />
      ) : (() => { const avs = getMissionAvatars(mission.id); return avs.length > 0 ? (
        <View style={[styles.missionCoverThumb, styles.avatarBand, { backgroundColor: '#0F172A' }]}>
          <View style={styles.avatarBandRow}>
            {avs.map((av, idx) => (
              <View key={idx} style={[styles.avatarBandCircle, idx > 0 && styles.avatarBandOverlap]}>
                <Image source={av} style={styles.avatarBandImg} resizeMode="cover" />
              </View>
            ))}
          </View>
        </View>
      ) : null; })()}
      <View style={styles.missionMeta}>
        <View style={styles.difficultyContainer}>
          <View style={[styles.difficultyDot, { backgroundColor: getDifficultyColor(mission.difficulty) }]} />
          <Text style={[styles.difficultyText, { color: colors.textSecondary }]}>{mission.difficulty}</Text>
        </View>
        <View style={styles.timeContainer}>
          <Ionicons name="time" size={12} color={colors.iconSecondary} />
          <Text style={[styles.timeText, { color: colors.textSecondary }]}> {mission.timeLeft}</Text>
        </View>
      </View>
      <View style={styles.progressContainer}>
        <View style={styles.progressHeader}>
          <Text style={[styles.progressLabel, { color: colors.textSecondary }]}>Progress</Text>
          <Text style={[styles.progressPercentage, { color: colors.textPrimary }]}>{mission.progress}%</Text>
        </View>
        <View style={[styles.progressBar, { backgroundColor: colors.border }]}>
          <View style={[styles.progressFill, { width: `${mission.progress}%` as any, backgroundColor: colors.primary }]} />
        </View>
      </View>
      <View style={styles.actionButtons}>
        <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.actionButtonBackground }]} onPress={() => navigation.navigate('Chapters' as never, { missionId: mission.id } as never)}>
          <Ionicons name="book" size={16} color={colors.iconPrimary} />
          <Text style={[styles.actionButtonText, { color: colors.actionButtonText }]}>Chapters</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.actionButtonBackground }]} onPress={() => navigation.navigate('MissionChat' as never, { missionId: mission.id } as never)}>
          <Ionicons name="chatbox" size={16} color={colors.iconPrimary} />
          <Text style={[styles.actionButtonText, { color: colors.actionButtonText }]}>Chat</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.actionButtonBackground }]} onPress={() => navigation.navigate('MissionCards' as never, { missionId: mission.id } as never)}>
          <Ionicons name="document" size={16} color={colors.iconPrimary} />
          <Text style={[styles.actionButtonText, { color: colors.actionButtonText }]}>Cards</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.actionButtonBackground }]} onPress={() => navigation.navigate('UserStories' as never, { missionId: mission.id } as never)}>
          <Ionicons name="people" size={16} color={colors.iconPrimary} />
          <Text style={[styles.actionButtonText, { color: colors.actionButtonText }]}>Stories</Text>
        </TouchableOpacity>
      </View>
      <TouchableOpacity style={[styles.detailsButton, { borderColor: colors.borderSecondary }]} onPress={() => navigation.navigate('MissionDetail' as never, { missionId: mission.id } as never)}>
        <Ionicons name="information-circle-outline" size={16} color={colors.textSecondary} />
        <Text style={[styles.detailsButtonText, { color: colors.textSecondary }]}>Plus de details</Text>
      </TouchableOpacity>
      <View style={[styles.trialLimitations, { backgroundColor: isDarkTheme ? 'rgba(120,53,15,0.15)' : '#FEF3C7' }]}>
        <Text style={[styles.limitationsTitle, { color: isDarkTheme ? '#FCD34D' : '#92400E' }]}>Trial Limitations:</Text>
        {mission.trialLimitations.map((lim, i) => (
          <Text key={i} style={[styles.limitationText, { color: isDarkTheme ? '#FCD34D' : '#92400E' }]}>- {lim}</Text>
        ))}
      </View>
      <View style={styles.trialActions}>
        <TouchableOpacity
          style={[styles.inTrialButton, { backgroundColor: colors.primary + '22', borderColor: colors.primary, borderWidth: 1.5 }]}
          onPress={() => navigation.navigate('ChapterDetail' as never, { missionId: mission.id, chapterId: 1 } as never)}
        >
          <Ionicons name="play-circle" size={16} color={colors.primary} />
          <Text style={[styles.inTrialButtonText, { color: colors.primary }]}>In Trial - Ouvrir</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.buyButton, { backgroundColor: isInCart(mission.id) ? colors.success : colors.primary }]}
          onPress={() => { if (!isInCart(mission.id)) void addToCart(mission.id); }}
          disabled={isInCart(mission.id)}
        >
          <Ionicons name={isInCart(mission.id) ? 'checkmark-circle' : 'cart'} size={16} color={colors.badgeText} />
          <Text style={[styles.buyButtonText, { color: colors.badgeText }]}> {isInCart(mission.id) ? 'In Cart' : `$${mission.fullPrice}`}</Text>
        </TouchableOpacity>
      </View>
      <TouchableOpacity
        style={[styles.cancelTrialButton, { borderColor: colors.error }]}
        onPress={() => cancelTrial(mission.id)}
      >
        <Ionicons name="close-circle-outline" size={16} color={colors.error} />
        <Text style={[styles.cancelTrialButtonText, { color: colors.error }]}>Cancel Trial</Text>
      </TouchableOpacity>
    </View>
  );

  const renderMarketplaceMissionCard = (mission: MarketplaceMission) => {
    const inTrial = isInTrial(mission.id);
    return (
      <View key={mission.id} style={[styles.courseCard, { backgroundColor: colors.cardBackground }]}>
        <View style={styles.coverWrapper}>
          {mission.coverImage ? (
            <Image source={mission.coverImage} style={styles.coverImage} resizeMode="cover" />
          ) : (() => { const avs = getMissionAvatars(mission.id); return avs.length > 0 ? (
            <View style={[styles.coverImagePlaceholder, { backgroundColor: '#0F172A' }]}>
              <View style={styles.avatarBandRow}>
                {avs.map((av, idx) => (
                  <View key={idx} style={[styles.avatarBandCircle, idx > 0 && styles.avatarBandOverlap]}>
                    <Image source={av} style={styles.avatarBandImg} resizeMode="cover" />
                  </View>
                ))}
              </View>
            </View>
          ) : (
            <View style={[styles.coverImagePlaceholder, { backgroundColor: colors.surfaceSecondary }]}>
              <Ionicons name="image-outline" size={32} color={colors.textTertiary} />
            </View>
          ); })()}
          {mission.badgeImage && !failedBadges.has(mission.id) && (
            <View style={styles.badgeOverlay}>
              <Image
                source={mission.badgeImage}
                style={styles.badgeOverlayImage}
                resizeMode="contain"
                onError={() => markBadgeFailed(mission.id)}
              />
            </View>
          )}
        </View>
        <View style={styles.cardBody}>
          <View style={styles.cardHeader}>
            <View style={styles.titleRowContainer}>
              <View style={styles.titleFlex}>
                <Text style={[styles.courseTitle, { color: colors.textPrimary }]}>{mission.title}</Text>
                {mission.badgeImage && !failedBadges.has(mission.id) && (
                  <Image
                    source={mission.badgeImage}
                    style={styles.titleBadgeImage}
                    resizeMode="contain"
                    onError={() => markBadgeFailed(mission.id)}
                  />
                )}
              </View>
              <View style={styles.cardBadgesRow}>
                {mission.tags.certification && (
                  <View style={[styles.certBadge, { backgroundColor: colors.primary }]}>
                    <Text style={[styles.certBadgeText, { color: colors.badgeText }]}>{mission.tags.certification}</Text>
                  </View>
                )}
                {mission.isOnSale && (
                  <View style={[styles.saleBadge, { backgroundColor: colors.error }]}>
                    <Text style={[styles.saleText, { color: colors.badgeText }]}>Sale</Text>
                  </View>
                )}
              </View>
            </View>
            {mission.subtitle ? <Text style={[styles.courseSubtitle, { color: colors.textSecondary }]} numberOfLines={2}>{mission.subtitle}</Text> : null}
          </View>
          <View style={styles.metaContainer}>
            <View style={styles.difficultyContainer}>
              <View style={[styles.difficultyDot, { backgroundColor: getDifficultyColor(mission.difficulty) }]} />
              <Text style={[styles.difficultyText, { color: colors.textSecondary }]}>{mission.difficulty}</Text>
            </View>
            <Text style={[styles.durationText, { color: colors.textSecondary }]}>{mission.duration}</Text>
          </View>
          <View style={styles.ratingContainer}>
            <View style={styles.starsContainer}>{renderStars(mission.rating)}</View>
            <Text style={[styles.ratingText, { color: colors.textPrimary }]}>{mission.rating}</Text>
            <Text style={[styles.reviewsText, { color: colors.textSecondary }]}>({mission.reviews} reviews)</Text>
          </View>
          <View style={styles.priceContainer}>
            <View style={styles.priceInfo}>
              {mission.originalPrice && <Text style={[styles.originalPrice, { color: colors.textTertiary }]}>${mission.originalPrice}</Text>}
              <Text style={[styles.currentPrice, { color: colors.textPrimary }]}>${mission.price}</Text>
            </View>
            <View style={[styles.categoryBadge, { backgroundColor: colors.surfaceSecondary }]}>
              <Text style={[styles.categoryText, { color: colors.textSecondary }]}>{mission.category}</Text>
            </View>
          </View>
          <TouchableOpacity style={[styles.detailsButton, { borderColor: colors.borderSecondary, marginBottom: 8 }]} onPress={() => navigation.navigate('MissionDetail' as never, { missionId: mission.id } as never)}>
            <Ionicons name="information-circle-outline" size={16} color={colors.textSecondary} />
            <Text style={[styles.detailsButtonText, { color: colors.textSecondary }]}>Plus de details</Text>
          </TouchableOpacity>
          <View style={styles.cardActionButtons}>
            <TouchableOpacity
              style={[styles.startTrialButton, { backgroundColor: inTrial ? colors.success + '22' : colors.surfaceSecondary, borderColor: inTrial ? colors.success : colors.borderSecondary }]}
              onPress={() => { if (!inTrial) { startTrial(mission.id); setActiveTab('trial'); } }}
              disabled={inTrial}
            >
              <Ionicons name={inTrial ? 'checkmark-circle' : 'play'} size={16} color={inTrial ? colors.success : colors.textPrimary} />
              <Text style={[styles.startTrialText, { color: inTrial ? colors.success : colors.textPrimary }]}>{inTrial ? 'In Trial' : 'Start Trial'}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.buyNowButton, { backgroundColor: isInCart(mission.id) ? colors.success : colors.primary }]}
              onPress={() => { if (!isInCart(mission.id)) void addToCart(mission.id); }}
              disabled={isInCart(mission.id)}
            >
              <Ionicons name={isInCart(mission.id) ? 'checkmark-circle' : 'cart'} size={16} color={colors.badgeText} />
              <Text style={[styles.buyNowText, { color: colors.badgeText }]}>{isInCart(mission.id) ? 'In Cart' : 'Buy Now'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const filteredMissions = getFilteredMissions();
  // Catalogue visible (hors missions déjà acquises) : sert de total cohérent
  // pour l'onglet et la barre de résultats quand aucun filtre de tag n'est posé.
  const marketplaceVisibleCount = allMarketplaceMissions.filter(m => !isOwned(m.id)).length;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <FontAwesome name="bullseye" size={24} color={colors.iconPrimary} />
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Missions</Text>
        </View>
        <View style={styles.headerSubtitle}>
          <Text style={[styles.subtitleText, { color: colors.textSecondary }]}>
            {activeTab === 'active' ? 'Manage your active missions' : activeTab === 'trial' ? 'Your trial missions with limited access' : 'Discover new mission opportunities'}
          </Text>
        </View>
        {!user && (
          <TouchableOpacity style={{ backgroundColor: '#4F46E5', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8, marginRight: 8 }} onPress={() => navigation.navigate('Login' as never)}>
            <Text style={{ color: '#FFF', fontWeight: '600', fontSize: 14 }}>Se connecter</Text>
          </TouchableOpacity>
        )}
        {/* La barre globale DesktopLayout (avec panier) n'existe QUE pour un
            utilisateur connecté en desktop. On affiche donc ce panier sinon :
            mobile, OU invité desktop (PublicNavigator, sans barre globale). */}
        {(!isDesktop || !user) && (
          <TouchableOpacity style={styles.cartButton} onPress={() => navigation.navigate('Cart' as never)}>
            <Ionicons name="cart" size={24} color={colors.iconPrimary} />
            {cartItems.length > 0 && (
              <View style={[styles.cartBadge, { backgroundColor: colors.error }]}>
                <Text style={[styles.cartBadgeText, { color: '#FFF' }]}>{cartItems.length}</Text>
              </View>
            )}
          </TouchableOpacity>
        )}
      </View>

      <View style={[styles.tabsContainer, { backgroundColor: colors.surface }]}>
        {user && (
          <TouchableOpacity style={[styles.tab, { backgroundColor: activeTab === 'active' ? colors.tabActiveBackground : 'transparent' }, activeTab === 'active' && styles.activeTab]} onPress={() => setActiveTab('active')}>
            <Text style={[styles.tabText, { color: activeTab === 'active' ? colors.tabActive : colors.tabInactive }]}>Missions actives ({activeMissions.length})</Text>
          </TouchableOpacity>
        )}
        {user && (
          <TouchableOpacity style={[styles.tab, { backgroundColor: activeTab === 'trial' ? colors.tabActiveBackground : 'transparent' }, activeTab === 'trial' && styles.activeTab]} onPress={() => setActiveTab('trial')}>
            <Text style={[styles.tabText, { color: activeTab === 'trial' ? colors.tabActive : colors.tabInactive }]}>Trial ({trialMissions.length})</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity style={[styles.tab, { backgroundColor: activeTab === 'marketplace' ? colors.tabActiveBackground : 'transparent' }, activeTab === 'marketplace' && styles.activeTab]} onPress={() => setActiveTab('marketplace')}>
          <Text style={[styles.tabText, { color: activeTab === 'marketplace' ? colors.tabActive : colors.tabInactive }]}>Marketplace ({marketplaceVisibleCount})</Text>
        </TouchableOpacity>
      </View>

      {activeTab === 'marketplace' && (
        <View
          style={[
            styles.filtersContainer,
            { backgroundColor: colors.surface, borderBottomColor: colors.border },
            isWebDesktop && styles.filtersContainerDesktop,
          ]}
        >
          <View style={styles.filterHeader}>
            <Ionicons name="filter" size={16} color={colors.iconSecondary} />
            <Text style={[styles.filterTitle, { color: colors.textPrimary }]}>Filter by Tags</Text>
            {hasActiveFilters && (
              <TouchableOpacity style={styles.clearButton} onPress={clearFilters}>
                <Ionicons name="close" size={14} color={colors.iconSecondary} />
                <Text style={[styles.clearButtonText, { color: colors.textSecondary }]}>Clear</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={() => setFiltersCollapsed(c => !c)} style={styles.filterChevron} activeOpacity={0.7}>
              <Ionicons name={filtersCollapsed ? 'chevron-down' : 'chevron-up'} size={18} color={colors.iconSecondary} />
            </TouchableOpacity>
          </View>
          {!filtersCollapsed && (
            <>
              <Text style={[styles.filterResultsHint, { color: colors.textSecondary }]}>
                {filteredMissions.length} resultat(s) trouve(s){filteredMissions.length > 0 ? ' — faites defiler pour voir les cartes.' : ''}
              </Text>
              <ScrollView
                style={Platform.OS !== 'web' ? styles.filterScroll : undefined}
                nestedScrollEnabled
                showsVerticalScrollIndicator={false}
              >
                <View style={styles.filterSection}>
                  <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>Domain</Text>
                  <View style={styles.filterOptions}>
                    {['Cloud', 'Science', 'Languages'].map((domain) => (
                      <TouchableOpacity
                        key={domain}
                        style={[styles.filterOption, { backgroundColor: tagFilters.domain === domain ? colors.primary : colors.surfaceSecondary }]}
                        onPress={() => handleFilterChange('domain', tagFilters.domain === domain ? '' : domain)}
                      >
                        <Text style={[styles.filterOptionText, { color: tagFilters.domain === domain ? colors.badgeText : colors.textSecondary }]}>{domain}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                {tagFilters.domain !== '' && (() => {
                  const groups = CERT_GROUPS[tagFilters.domain] || [];
                  // Domaine multi-provider (Cloud) : on choisit le provider AVANT la certif.
                  if (groups.length > 1) {
                    const selectedGroup = groups.find(g => g.label === tagFilters.provider);
                    return (
                      <>
                        <View style={styles.filterSection}>
                          <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>Cloud provider</Text>
                          <View style={styles.filterOptions}>
                            {groups.map((group) => (
                              <TouchableOpacity
                                key={group.label}
                                style={[styles.filterOption, { backgroundColor: tagFilters.provider === group.label ? colors.primary : colors.surfaceSecondary }]}
                                onPress={() => handleFilterChange('provider', tagFilters.provider === group.label ? '' : group.label)}
                              >
                                <Text style={[styles.filterOptionText, { color: tagFilters.provider === group.label ? colors.badgeText : colors.textSecondary }]}>{group.label}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        </View>
                        {selectedGroup && (
                          <View style={styles.filterSection}>
                            <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>Certifications</Text>
                            <View style={styles.filterOptions}>
                              {selectedGroup.items.map((cert) => (
                                <TouchableOpacity
                                  key={cert}
                                  style={[styles.filterOption, { backgroundColor: tagFilters.certification === cert ? colors.primary : colors.surfaceSecondary }]}
                                  onPress={() => handleFilterChange('certification', tagFilters.certification === cert ? '' : cert)}
                                >
                                  <Text style={[styles.filterOptionText, { color: tagFilters.certification === cert ? colors.badgeText : colors.textSecondary }]}>{cert}</Text>
                                </TouchableOpacity>
                              ))}
                            </View>
                          </View>
                        )}
                      </>
                    );
                  }
                  // Domaine simple (Science / Languages) : items directs.
                  return (
                    <View style={styles.filterSection}>
                      <Text style={[styles.filterLabel, { color: colors.textSecondary }]}>Topics</Text>
                      <View style={styles.filterOptions}>
                        {groups.flatMap(g => g.items).map((cert) => (
                          <TouchableOpacity
                            key={cert}
                            style={[styles.filterOption, { backgroundColor: tagFilters.certification === cert ? colors.primary : colors.surfaceSecondary }]}
                            onPress={() => handleFilterChange('certification', tagFilters.certification === cert ? '' : cert)}
                          >
                            <Text style={[styles.filterOptionText, { color: tagFilters.certification === cert ? colors.badgeText : colors.textSecondary }]}>{cert}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  );
                })()}
              </ScrollView>
            </>
          )}
        </View>
      )}

      <ScrollView style={[styles.content, { backgroundColor: colors.background }]} contentContainerStyle={{ width: '100%', flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        {activeTab === 'marketplace' && (
          <View style={[styles.resultsBar, { backgroundColor: colors.surfaceSecondary, borderColor: colors.borderSecondary }]}>
            <Text style={[styles.resultsBarText, { color: colors.textSecondary }]}>
              {marketplaceLoading && allMarketplaceMissions.length === 0
                ? 'Chargement…'
                : `${filteredMissions.length} resultat(s) / ${marketplaceVisibleCount}`}
            </Text>
          </View>
        )}
        <View style={isWebDesktop ? [styles.missionsList, styles.desktopThreeColGrid] : styles.missionsList}>
          {activeTab === 'active' && activeMissions.map(renderActiveMissionCard)}
          {activeTab === 'trial' && trialMissions.map(renderTrialMissionCard)}
          {activeTab === 'marketplace' && filteredMissions.map(renderMarketplaceMissionCard)}
          {activeTab === 'marketplace' && filteredMissions.length === 0 && marketplaceLoading && (
            <View style={[styles.emptyState, { backgroundColor: colors.surfaceSecondary, borderColor: colors.borderSecondary }]}>
              <ActivityIndicator color={colors.primary} />
              <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                Chargement du marketplace…
              </Text>
            </View>
          )}
          {activeTab === 'marketplace' && filteredMissions.length === 0 && !marketplaceLoading && (
            <View style={[styles.emptyState, { backgroundColor: colors.surfaceSecondary, borderColor: colors.borderSecondary }]}>
              <Ionicons name="search" size={16} color={colors.iconSecondary} />
              <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                Aucun resultat pour ce filtre.
              </Text>
              <TouchableOpacity onPress={clearFilters}>
                <Text style={[styles.emptyStateAction, { color: colors.primary }]}>Reinitialiser</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  headerContent: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  headerTitle: { fontSize: 24, fontWeight: 'bold', marginLeft: 8 },
  headerSubtitle: { flex: 1, marginLeft: 16 },
  subtitleText: { fontSize: 14 },
  cartButton: { position: 'relative', padding: 8 },
  cartBadge: { position: 'absolute', top: 0, right: 0, borderRadius: 10, width: 20, height: 20, justifyContent: 'center', alignItems: 'center' },
  cartBadgeText: { fontSize: 12, fontWeight: 'bold' },
  tabsContainer: { flexDirection: 'row', marginHorizontal: 16, marginTop: 16, borderRadius: 8, padding: 4 },
  tab: { flex: 1, paddingVertical: 8, paddingHorizontal: 12, borderRadius: 6, alignItems: 'center' },
  activeTab: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 2 },
  tabText: { fontSize: 14, fontWeight: '500' },
  filtersContainer: { padding: 16, borderBottomWidth: 1 },
  filtersContainerDesktop: { maxHeight: 280, overflow: 'auto' } as any,
  filterScroll: { maxHeight: 280 }, // natif : rend les filtres défilables (Android)
  filterHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  filterTitle: { fontSize: 14, fontWeight: '600', marginLeft: 8, flex: 1 },
  filterChevron: { marginLeft: 8, padding: 4 },
  filterResultsHint: { fontSize: 12, marginBottom: 10 },
  clearButton: { flexDirection: 'row', alignItems: 'center', marginLeft: 'auto' as any },
  clearButtonText: { fontSize: 12, marginLeft: 4 },
  filterSection: { marginBottom: 12 },
  filterLabel: { fontSize: 12, fontWeight: '600', marginBottom: 8 },
  filterOptions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  filterOption: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16 },
  filterOptionText: { fontSize: 12 },
  certGroup: { marginBottom: 10 },
  certGroupLabel: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  content: { flex: 1 },
  missionsList: { padding: 16 },
  missionCard: { borderRadius: 12, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3, elevation: 2 },
  missionHeader: { marginBottom: 12 },
  missionTitleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 4 },
  missionTitleFlex: { flex: 2 },
  missionBadgesFlex: { flex: 1, alignItems: 'flex-end', flexWrap: 'wrap' },
  missionTitle: { fontSize: 18, fontWeight: 'bold' },
  missionSubtitle: { fontSize: 13, fontStyle: 'italic', marginTop: 2, lineHeight: 18 },
  missionDescription: { fontSize: 14, lineHeight: 20 },
  missionBadges: { alignItems: 'flex-end' },
  missionInfo: { flex: 1, marginRight: 12 },
  titleContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 },
  trialBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, marginBottom: 4 },
  trialText: { fontSize: 12, fontWeight: '600' },
  rewardBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, marginBottom: 4 },
  rewardText: { fontSize: 12, fontWeight: '600' },
  certPill: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: 'rgba(99,102,241,0.18)', marginTop: 4 },
  certPillText: { fontSize: 11, fontWeight: '700', color: '#6366F1', letterSpacing: 0.5 },
  missionCoverThumb: { width: '100%', height: 100, borderRadius: 8, marginBottom: 12 } as any,
  missionMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  difficultyContainer: { flexDirection: 'row', alignItems: 'center' },
  difficultyDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
  difficultyText: { fontSize: 12 },
  timeContainer: { flexDirection: 'row', alignItems: 'center' },
  timeText: { fontSize: 12 },
  progressContainer: { marginBottom: 12 },
  progressHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  progressLabel: { fontSize: 14 },
  progressPercentage: { fontSize: 14, fontWeight: '600' },
  progressBar: { height: 6, borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%' as any, borderRadius: 3 },
  actionButtons: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12, gap: 8 },
  actionButton: { flex: 1, paddingVertical: 10, paddingHorizontal: 8, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 },
  actionButtonText: { fontSize: 12, fontWeight: '500' },
  trialLimitations: { marginBottom: 12, padding: 12, borderRadius: 6 },
  limitationsTitle: { fontSize: 12, fontWeight: '600', marginBottom: 4 },
  limitationText: { fontSize: 11, lineHeight: 16 },
  claimButton: { paddingVertical: 10, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', marginTop: 8, gap: 6 },
  claimButtonText: { fontSize: 14, fontWeight: '600' },
  continueButton: { paddingVertical: 10, borderRadius: 8, alignItems: 'center', marginTop: 8 },
  continueButtonText: { fontSize: 14, fontWeight: '600' },
  trialActions: { flexDirection: 'row', gap: 8, marginTop: 8 },
  inTrialButton: { flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 },
  inTrialButtonText: { fontSize: 14, fontWeight: '600' },
  buyButton: { flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 },
  buyButtonText: { fontSize: 14, fontWeight: '600' },
  cancelTrialButton: { marginTop: 8, paddingVertical: 10, borderRadius: 8, borderWidth: 1.5, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 },
  cancelTrialButtonText: { fontSize: 14, fontWeight: '600' },
  courseCard: { borderRadius: 12, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3, elevation: 2, overflow: 'hidden' },
  coverWrapper: { position: 'relative' },
  coverImage: { width: '100%' as any, height: 160 },
  coverImagePlaceholder: { width: '100%' as any, height: 160, alignItems: 'center', justifyContent: 'center' },
  badgeOverlay: { position: 'absolute', top: 8, right: 8, width: 56, height: 56, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.95)', alignItems: 'center', justifyContent: 'center', padding: 4 },
  badgeOverlayImage: { width: 48, height: 48 },
  cardBody: { padding: 16 },
  cardHeader: { marginBottom: 12 },
  titleRowContainer: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 4 },
  titleFlex: { flex: 2, flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardBadgesRow: { flex: 1, flexDirection: 'row', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'flex-end', marginLeft: 8 },
  certBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  certBadgeText: { fontSize: 11, fontWeight: '700', letterSpacing: 0.3 },
  courseTitle: { fontSize: 18, fontWeight: 'bold' },
  courseSubtitle: { fontSize: 13, lineHeight: 18, fontStyle: 'italic', marginTop: 2 },
  saleBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  saleText: { fontSize: 11, fontWeight: '600' },
  metaContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  durationText: { fontSize: 12 },
  ratingContainer: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  starsContainer: { flexDirection: 'row', marginRight: 8 },
  ratingText: { fontSize: 14, fontWeight: '600', marginRight: 4 },
  reviewsText: { fontSize: 12 },
  priceContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  priceInfo: { flexDirection: 'row', alignItems: 'center' },
  originalPrice: { fontSize: 14, textDecorationLine: 'line-through', marginRight: 8 },
  currentPrice: { fontSize: 18, fontWeight: 'bold' },
  categoryBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
  categoryText: { fontSize: 12, fontWeight: '500' },
  cardActionButtons: { flexDirection: 'row', gap: 8 },
  startTrialButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderWidth: 1, paddingVertical: 10, borderRadius: 8, gap: 6 },
  startTrialText: { fontSize: 14, fontWeight: '600' },
  buyNowButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderRadius: 8, gap: 6 },
  buyNowText: { fontSize: 14, fontWeight: '600' },
  detailsButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 8, borderRadius: 8, borderWidth: 1, marginBottom: 8, gap: 6 },
  detailsButtonText: { fontSize: 13, fontWeight: '500' },
  titleWithBadge: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flex: 1, gap: 8 },
  titleBadgeImage: { width: 48, height: 48, flexShrink: 0 },
  avatarBand: { alignItems: 'center', justifyContent: 'center' },
  avatarBandRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  avatarBandCircle: { width: 50, height: 50, borderRadius: 25, overflow: 'hidden', borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)' },
  avatarBandOverlap: { marginLeft: -15 },
  avatarBandImg: { width: 50, height: 50, borderRadius: 25 },
  desktopThreeColGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', alignItems: 'stretch', gap: 16 } as any,
  resultsBar: { marginHorizontal: 16, marginTop: 12, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, borderWidth: 1 },
  resultsBarText: { fontSize: 12, fontWeight: '600' },
  emptyState: { width: '100%', borderWidth: 1, borderRadius: 10, padding: 16, alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 120 },
  emptyStateText: { fontSize: 13 },
  emptyStateAction: { fontSize: 13, fontWeight: '600' },
});




